import type { Store, StoreService } from '@libs/common-node';
import { isLocaleCA } from '@libs/core';

type ServiceUrlFunctions = {
    PetsHotel: (locale: string, storeNumber: number) => string;
    Adoptions: () => string;
    Veterinary: (locale: string) => string;
};

const STORE_DOMAIN = {
    US: 'https://stores.petsmart.com',
    CA: 'https://stores.petsmart.ca',
};

const SERVICE_URLS: ServiceUrlFunctions = {
    PetsHotel: (locale: string, storeNumber: number) => {
        const url =
            process.env.NEXT_PUBLIC_APP_ENV?.toLowerCase() === 'prod'
                ? `https://services.petsmart.${locale}/petshotel/${storeNumber}`
                : `https://services-staging.petsmart.${locale}/petshotel/${storeNumber}`;
        return url;
    },
    Adoptions: () => 'https://petsmartcharities.org/adopt-a-pet',
    Veterinary: (locale: string) =>
        `https://www.petsmart.${locale}/pet-services/petsmart-veterinary-care.html`,
};

const SERVICE_PATHS: Record<string, string> = {
    Training: 'dog-training',
    Grooming: 'grooming',
    'Doggie Day Camp': 'petshotel-doggie-day-camp',
};

export const servicesToDisplay = [
    'Veterinary',
    'PetsHotel',
    'Grooming',
    'Doggie Day Camp',
    'Training',
    'Adoptions',
];

const makeSnakeCase = (str: string) =>
    str
        .replace(/^(N\.|S\.|E\.|W\.)\s*/i, '')
        .replace(/[()]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .toLowerCase();

export function constructServiceURL(service: StoreService, store: Store) {
    const isLocaleCa = isLocaleCA();
    const locale = isLocaleCa ? 'ca' : 'com';
    const domain = isLocaleCa ? STORE_DOMAIN.CA : STORE_DOMAIN.US;

    if (service.ServiceName in SERVICE_PATHS) {
        return `${domain}/${store.StateProvinceAbbreviation.toLowerCase()}/${makeSnakeCase(
            store.City
        )}/${makeSnakeCase(store.Name)}/${SERVICE_PATHS[service.ServiceName]}`;
    }

    return SERVICE_URLS[service.ServiceName as keyof ServiceUrlFunctions]?.(
        locale,
        store.StoreNumber
    );
}

export const serviceImages: Record<string, string> = {
    Grooming:
        'https://images.petsmartassets.com/is/image/PetSmart/WEB-24-2287450-May24-StoreLoc-Grooming?fmt=webp&wid=60&hei=60',
    Adoptions:
        'https://images.petsmartassets.com/is/image/PetSmart/WEB-24-2287450-May24-StoreLoc-Adopt?fmt=webp&wid=60&hei=60',
    Training:
        'https://images.petsmartassets.com/is/image/PetSmart/WEB-24-2287450-May24-StoreLoc-Training?fmt=webp&wid=60&hei=60',
    'PetSmart Veterinary Services':
        'https://images.petsmartassets.com/is/image/PetSmart/WEB-24-2287450-May24-StoreLoc-Vet?fmt=webp&wid=60&hei=60',
    Veterinary:
        'https://images.petsmartassets.com/is/image/PetSmart/WEB-24-2287450-May24-StoreLoc-Vet?fmt=webp&wid=60&hei=60',
    'Doggie Day Camp':
        'https://images.petsmartassets.com/is/image/PetSmart/WEB-24-2287450-May24-StoreLoc-DDC?fmt=webp&wid=60&hei=60',
    PetsHotel:
        'https://images.petsmartassets.com/is/image/PetSmart/WEB-24-2287450-May24-StoreLoc-PetsHotel?fmt=webp&wid=60&hei=60',
};
